import type React from 'react';

import { Stack, Typography } from '@mui/material';
import { JsonSchemaInput } from '@ragu-components/json-schema-input';
import { OrdBulkUpdateFormButton, OrdFormList, OrdTabbedForm, OrdIconStandard } from '@ragu-components/ord-components';
import { ApConfigurationUpdateFields, dataSchema } from '@ragu-components/ord-types';
import {
  ArrayInput,
  PasswordInput,
  SelectArrayInput,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextInput,
  useTranslate,
} from 'react-admin';

import { OrdDescriptionInput } from 'src/components/formInputs';

import type { SxProps } from '@mui/material';
import type { JsonSchemaInputProps } from '@ragu-components/json-schema-input';
import type { OrdTabbedFormProps } from '@ragu-components/ord-components';

const arrayInputStyle: SxProps = {
  '& .RaArrayInput-label': { marginBottom: 1 },
  '& .RaSimpleFormIterator-line': { borderBottom: 'none !important' },
};

const codecChoices = dataSchema.ApConfigurationSettings.properties.codecs.items.enum.map((codec) => ({
  id: codec,
  name: codec,
}));

const srtpEnumValues = dataSchema.ApConfigurationSettings.properties.srtp.enum;

const ApConfigInput = (props: Omit<JsonSchemaInputProps, 'jsonSchema' | 'schemas'>): React.ReactElement => (
  <JsonSchemaInput jsonSchema={dataSchema.ApConfigurationUpdate} schemas={dataSchema} {...props} />
);

const SectionTitle = ({ label }: { label: string }): React.ReactElement => (
  <Typography color='text.secondary' sx={{ px: 2, pt: 1 }} variant='subtitle2'>
    {label}
  </Typography>
);

export const ApConfigurationsBulkEditForm = (): React.ReactElement => {
  const translate = useTranslate();

  const srtpChoices = srtpEnumValues.map((value) => ({
    id: value,
    name: translate(`resources.ap_configurations.srtpValues.${value}`),
  }));

  const inputs = [
    {
      label: 'resources.ap_configurations.fields.settings.sip_port',
      element: (
        <OrdFormList icon={<OrdIconStandard name='phone' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.sip_port']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.reg_expiry',
      element: (
        <OrdFormList icon={<OrdIconStandard name='phone' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.reg_expiry']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.outbound_proxy_port',
      element: (
        <OrdFormList icon={<OrdIconStandard name='phone' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.outbound_proxy_port']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.outbound_backup_proxy_port',
      element: (
        <OrdFormList icon={<OrdIconStandard name='phone' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.outbound_backup_proxy_port']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.sip_transport',
      element: (
        <OrdFormList icon={<OrdIconStandard name='phone' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.sip_transport']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.srtp',
      element: (
        <OrdFormList icon={<OrdIconStandard name='phone' />}>
          <SelectInput
            source={ApConfigurationUpdateFields['settings.srtp']}
            choices={srtpChoices}
            variant='outlined'
            fullWidth
            helperText={false}
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.mwi_voicemail_subscribe',
      element: (
        <OrdFormList icon={<OrdIconStandard name='phone' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.mwi_voicemail_subscribe']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.mwi_voicemail_number',
      element: (
        <OrdFormList icon={<OrdIconStandard name='phone' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.mwi_voicemail_number']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.call_waiting',
      element: (
        <OrdFormList icon={<OrdIconStandard name='password' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.call_waiting']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.display_method',
      element: (
        <OrdFormList icon={<OrdIconStandard name='password' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.display_method']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.ntp_server_1',
      element: (
        <OrdFormList icon={<OrdIconStandard name='network' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.ntp_server_1']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.ntp_server_2',
      element: (
        <OrdFormList icon={<OrdIconStandard name='network' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.ntp_server_2']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.fw_url',
      element: (
        <OrdFormList icon={<OrdIconStandard name='network' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.fw_url']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.ca_url',
      element: (
        <OrdFormList icon={<OrdIconStandard name='network' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.ca_url']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.vlan_wanport_enable',
      element: (
        <OrdFormList icon={<OrdIconStandard name='network' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.vlan_wanport_enable']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.vlan_wanport_vid',
      element: (
        <OrdFormList icon={<OrdIconStandard name='network' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.vlan_wanport_vid']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.vlan_pcport_enable',
      element: (
        <OrdFormList icon={<OrdIconStandard name='network' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.vlan_pcport_enable']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.vlan_pcport_vid',
      element: (
        <OrdFormList icon={<OrdIconStandard name='network' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.vlan_pcport_vid']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.dscp_enable',
      element: (
        <OrdFormList icon={<OrdIconStandard name='network' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.dscp_enable']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.dscp_voice',
      element: (
        <OrdFormList icon={<OrdIconStandard name='network' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.dscp_voice']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.dscp_signal',
      element: (
        <OrdFormList icon={<OrdIconStandard name='network' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.dscp_signal']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.codecs',
      element: (
        <OrdFormList icon={<OrdIconStandard name='dns' />}>
          <SelectArrayInput
            source={ApConfigurationUpdateFields['settings.codecs']}
            choices={codecChoices}
            variant='outlined'
            fullWidth
            helperText={false}
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.ldap_title',
      element: (
        <OrdFormList icon={<OrdIconStandard name='dns' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.ldap_title']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.ldap_host',
      element: (
        <OrdFormList icon={<OrdIconStandard name='dns' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.ldap_host']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.ldap_port',
      element: (
        <OrdFormList icon={<OrdIconStandard name='dns' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.ldap_port']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.ldap_base_search',
      element: (
        <OrdFormList icon={<OrdIconStandard name='dns' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.ldap_base_search']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.ldap_filter_name',
      element: (
        <OrdFormList icon={<OrdIconStandard name='dns' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.ldap_filter_name']} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.ap_configurations.fields.settings.ldap_filter_number',
      element: (
        <OrdFormList icon={<OrdIconStandard name='dns' />}>
          <ApConfigInput source={ApConfigurationUpdateFields['settings.ldap_filter_number']} />
        </OrdFormList>
      ),
    },
  ];
  return <OrdBulkUpdateFormButton inputs={inputs} />;
};

export const ApConfigurationsForm = (props: OrdTabbedFormProps): React.ReactElement => {
  const translate = useTranslate();

  const srtpChoices = srtpEnumValues.map((value) => ({
    id: value,
    name: translate(`resources.ap_configurations.srtpValues.${value}`),
  }));

  return (
    <OrdTabbedForm
      sanitizeFields
      replaceRelationships
      createJsonSchema={dataSchema.ApConfigurationCreate}
      editJsonSchema={dataSchema.ApConfigurationUpdate}
      {...props}
    >
      <TabbedForm.Tab label='resources.ap_configurations.operations.sections.details'>
        <Stack spacing={1} sx={{ width: '100%' }}>
          <SectionTitle label={translate('resources.ap_configurations.groups.details')} />
          <OrdFormList icon={<OrdIconStandard name='apConfigurations' />}>
            <JsonSchemaInput source={ApConfigurationUpdateFields.name} />
            <OrdDescriptionInput source={ApConfigurationUpdateFields.description} />
            <PasswordInput source={ApConfigurationUpdateFields['settings.admin_password']} />
          </OrdFormList>
        </Stack>
      </TabbedForm.Tab>
      <TabbedForm.Tab label='resources.ap_configurations.operations.sections.sip'>
        <Stack spacing={1} sx={{ width: '100%' }}>
          <SectionTitle label={translate('resources.ap_configurations.groups.sip_parameters')} />
          <OrdFormList icon={<OrdIconStandard name='phone' />}>
            <ApConfigInput source={ApConfigurationUpdateFields['settings.sip_port']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.reg_expiry']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.outbound_proxy_port']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.outbound_backup_proxy_port']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.sip_transport']} />
            <SelectInput
              source={ApConfigurationUpdateFields['settings.srtp']}
              choices={srtpChoices}
              variant='outlined'
              fullWidth
              helperText={false}
            />
          </OrdFormList>

          <SectionTitle label={translate('resources.ap_configurations.groups.voicemail_parameters')} />
          <OrdFormList icon={<OrdIconStandard name='phone' />}>
            <ApConfigInput source={ApConfigurationUpdateFields['settings.mwi_voicemail_subscribe']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.mwi_voicemail_number']} />
          </OrdFormList>
        </Stack>
      </TabbedForm.Tab>
      <TabbedForm.Tab label='resources.ap_configurations.operations.sections.device'>
        <Stack spacing={1} sx={{ width: '100%' }}>
          <SectionTitle label={translate('resources.ap_configurations.groups.generic_parameters')} />
          <OrdFormList icon={<OrdIconStandard name='password' />}>
            <JsonSchemaInput
              source={ApConfigurationUpdateFields['settings.display_name']}
              helperText='resources.ap_configurations.helperTexts.display_name'
            />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.call_waiting']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.display_method']} />
          </OrdFormList>

          <SectionTitle label={translate('resources.ap_configurations.groups.ntp_parameters')} />
          <OrdFormList icon={<OrdIconStandard name='network' />}>
            <ApConfigInput source={ApConfigurationUpdateFields['settings.ntp_server_1']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.ntp_server_2']} />
          </OrdFormList>

          <SectionTitle label={translate('resources.ap_configurations.groups.firmware')} />
          <OrdFormList icon={<OrdIconStandard name='network' />}>
            <ApConfigInput source={ApConfigurationUpdateFields['settings.fw_url']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.ca_url']} />
          </OrdFormList>
        </Stack>
      </TabbedForm.Tab>
      <TabbedForm.Tab label='resources.ap_configurations.operations.sections.network'>
        <Stack spacing={1} sx={{ width: '100%' }}>
          <SectionTitle label={translate('resources.ap_configurations.groups.vlan_parameters')} />
          <OrdFormList icon={<OrdIconStandard name='network' />}>
            <ApConfigInput source={ApConfigurationUpdateFields['settings.vlan_wanport_enable']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.vlan_wanport_vid']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.vlan_pcport_enable']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.vlan_pcport_vid']} />
          </OrdFormList>

          <SectionTitle label={translate('resources.ap_configurations.groups.dscp')} />
          <OrdFormList icon={<OrdIconStandard name='network' />}>
            <ApConfigInput source={ApConfigurationUpdateFields['settings.dscp_enable']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.dscp_voice']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.dscp_signal']} />
          </OrdFormList>

          <SectionTitle label={translate('resources.ap_configurations.groups.multicast_addresses')} />
          <OrdFormList icon={<OrdIconStandard name='dns' />}>
            <ArrayInput source={ApConfigurationUpdateFields['settings.mcast_addr']} sx={arrayInputStyle}>
              <SimpleFormIterator fullWidth disableReordering disableClear>
                <TextInput
                  source=''
                  label={false}
                  helperText={false}
                  fullWidth
                  InputLabelProps={{ shrink: false }}
                  sx={{ mb: 1 }}
                />
              </SimpleFormIterator>
            </ArrayInput>
            <ArrayInput source={ApConfigurationUpdateFields['settings.mcast_label']} sx={arrayInputStyle}>
              <SimpleFormIterator fullWidth disableReordering disableClear>
                <TextInput
                  source=''
                  label={false}
                  helperText={false}
                  fullWidth
                  InputLabelProps={{ shrink: false }}
                  sx={{ mb: 1 }}
                />
              </SimpleFormIterator>
            </ArrayInput>
          </OrdFormList>
        </Stack>
      </TabbedForm.Tab>
      <TabbedForm.Tab label='resources.ap_configurations.operations.sections.media'>
        <Stack spacing={1} sx={{ width: '100%' }}>
          <SectionTitle label={translate('resources.ap_configurations.groups.dsp_parameters')} />
          <OrdFormList icon={<OrdIconStandard name='dns' />}>
            <SelectArrayInput
              source={ApConfigurationUpdateFields['settings.codecs']}
              choices={codecChoices}
              variant='outlined'
              fullWidth
              helperText={false}
            />
          </OrdFormList>
        </Stack>
      </TabbedForm.Tab>
      <TabbedForm.Tab label='resources.ap_configurations.operations.sections.directory'>
        <Stack spacing={1} sx={{ width: '100%' }}>
          <SectionTitle label={translate('resources.ap_configurations.groups.ldap_parameters')} />
          <OrdFormList icon={<OrdIconStandard name='dns' />}>
            <ApConfigInput source={ApConfigurationUpdateFields['settings.ldap_title']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.ldap_host']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.ldap_port']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.ldap_base_search']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.ldap_filter_name']} />
            <ApConfigInput source={ApConfigurationUpdateFields['settings.ldap_filter_number']} />
          </OrdFormList>
        </Stack>
      </TabbedForm.Tab>
    </OrdTabbedForm>
  );
};
